import { PackSortConfig, PackSortOrder } from '@/contexts/dynamic-config';
import { PackUnit } from '@/enums';

// Pack-unit display ordering. See https://gitlab.com/usepepper/product/-/issues/2955.

const DEFAULT_PACK_ORDER: PackUnit[] = [
  PackUnit.Case,
  PackUnit.HalfCase,
  PackUnit.Bin,
  PackUnit.Pack,
  PackUnit.Quarter,
  PackUnit.ClamshellBox,
  PackUnit.Bag,
  PackUnit.Each,
  PackUnit.Pound,
  PackUnit.Count,
  PackUnit.Unit,
];

// ASC = small → large, per the UoM Sorting PRD (https://docs.google.com/document/d/1WP2WuL2cIhj2VHKY5Ucr41HN6mgLJsJyaV148Vz_gwM/edit?tab=t.0#heading=h.ayxom4vymo9h).
const ASC_PACK_ORDER: PackUnit[] = [
  PackUnit.Inner,
  PackUnit.Bottle,
  PackUnit.Jar,
  PackUnit.Can,
  PackUnit.Tub,
  PackUnit.Block,
  PackUnit.Jug,
  PackUnit.Bucket,
  PackUnit.Pail,
  PackUnit.Count,
  PackUnit.Packet,
  PackUnit.Unit,
  PackUnit.Each,
  PackUnit.Piece,
  PackUnit.Gram,
  PackUnit.Ounce,
  PackUnit.Lb,
  PackUnit.Pound,
  PackUnit.Kilogram,
  PackUnit.Gallon,
  PackUnit.Bag,
  PackUnit.Basket,
  PackUnit.Package,
  PackUnit.Carton,
  PackUnit.HalfCase,
  PackUnit.Box,
  PackUnit.Crate,
  PackUnit.Layer,
  PackUnit.Bin,
  PackUnit.Case,
  PackUnit.Pallet,
];

// DESC = large → small.
const DESC_PACK_ORDER: PackUnit[] = [...ASC_PACK_ORDER].reverse();

const buildUnitIndexMap = (units: PackUnit[]): { [unit: string]: number } =>
  units.reduce<{ [unit: string]: number }>((map, unit, i) => {
    map[unit] = i;
    return map;
  }, {});
const DEFAULT_INDEX = buildUnitIndexMap(DEFAULT_PACK_ORDER);
const ASC_INDEX = buildUnitIndexMap(ASC_PACK_ORDER);
const DESC_INDEX = buildUnitIndexMap(DESC_PACK_ORDER);

enum PackSortTier {
  Custom = 0,
  List = 1,
  Alphabetical = 2,
  Tag = 3,
}

export interface ResolvedPackSort {
  customOrder: string[];
  sortOrder?: PackSortOrder;
}

export const resolvePackSortConfig = (
  config?: PackSortConfig | null,
): ResolvedPackSort => ({
  customOrder: config?.custom_order ?? [],
  sortOrder: config?.sort_order ?? undefined,
});

const getPackSortKey = (
  unit: PackUnit,
  { customOrder, sortOrder }: ResolvedPackSort,
): [tier: PackSortTier, index: number] => {
  // custom_order pins units to the front.
  const customIndex = customOrder.indexOf(unit);
  if (customIndex !== -1) {
    return [PackSortTier.Custom, customIndex];
  }

  // TAG always sorts last.
  if (unit === PackUnit.Tag) {
    return [PackSortTier.Tag, 0];
  }

  // Base list: ASC/DESC only when custom_order is empty, else DEFAULT.
  let indexMap = DEFAULT_INDEX;
  if (customOrder.length === 0) {
    if (sortOrder === PackSortOrder.Asc) {
      indexMap = ASC_INDEX;
    } else if (sortOrder === PackSortOrder.Desc) {
      indexMap = DESC_INDEX;
    }
  }
  const index = indexMap[unit];
  if (index !== undefined) {
    return [PackSortTier.List, index];
  }

  // Alphabetical tail, tiebroken by display string later.
  return [PackSortTier.Alphabetical, 0];
};

// `id` (e.g. variant pack UUID) is the final tiebreak, so packs that share a
// unit and display string (e.g. a product's SRP / No SRP Tag packs, which
// differ only in backend-driven list-item display text) still sort
// deterministically instead of following whatever order the backend returned.
export interface PackSortKey {
  displayString: string;
  id: string;
  unit: PackUnit;
}

export const comparePacksByUnit = (
  a: PackSortKey,
  b: PackSortKey,
  config: ResolvedPackSort,
): number => {
  const [aTier, aIndex] = getPackSortKey(a.unit, config);
  const [bTier, bIndex] = getPackSortKey(b.unit, config);

  if (aTier !== bTier) {
    return aTier - bTier;
  }
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  return (
    a.displayString.localeCompare(b.displayString) || a.id.localeCompare(b.id)
  );
};

// Fixed ASC ordering, ignoring any supplier-level display sort config: used to
// pick the physically-smallest pack (e.g. scan multi-match auto-select), which
// must not vary with a supplier's merchandising display preference.
const LOWEST_PACK_SORT_CONFIG: ResolvedPackSort = {
  customOrder: [],
  sortOrder: PackSortOrder.Asc,
};

export interface LowestPackKey extends PackSortKey {
  unitCount?: number | null;
}

export const compareLowestPack = (
  a: LowestPackKey,
  b: LowestPackKey,
): number => {
  const [aTier, aIndex] = getPackSortKey(a.unit, LOWEST_PACK_SORT_CONFIG);
  const [bTier, bIndex] = getPackSortKey(b.unit, LOWEST_PACK_SORT_CONFIG);

  if (aTier !== bTier) {
    return aTier - bTier;
  }
  if (aIndex !== bIndex) {
    return aIndex - bIndex;
  }
  const aCount = a.unitCount ?? 1;
  const bCount = b.unitCount ?? 1;
  if (aCount !== bCount) {
    return aCount - bCount;
  }
  return (
    a.displayString.localeCompare(b.displayString) || a.id.localeCompare(b.id)
  );
};

// Returns the single item that sorts lowest by pack size, or null for an empty list.
export const selectLowestPack = <T>(
  items: T[],
  toLowestPackKey: (item: T) => LowestPackKey,
): T | null =>
  items.reduce<T | null>(
    (lowest, item) =>
      lowest === null ||
      compareLowestPack(toLowestPackKey(item), toLowestPackKey(lowest)) < 0
        ? item
        : lowest,
    null,
  );

// Groups items by product identity, preserving each group's first-encountered
// position (e.g. search relevance order). Used for flat (ungrouped) multi-match
// lists like the scanner picker, where a match set can span multiple different
// products: sorting the whole list by pack order alone would otherwise
// interleave different products' packs instead of keeping each product's
// packs together.
const groupByKey = <T>(items: T[], toGroupKey: (item: T) => string): T[][] => {
  const groupOrder: string[] = [];
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const key = toGroupKey(item);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
      groupOrder.push(key);
    }
  }

  return groupOrder.map((key) => groups.get(key)!);
};

// Groups by product, then sorts each group lowest → highest UOM (fixed ASC
// order, ignoring supplier display-sort config) — matches what scan
// auto-select would have picked.
export const groupAndSortByLowestPack = <T>(
  items: T[],
  toGroupKey: (item: T) => string,
  toLowestPackKey: (item: T) => LowestPackKey,
): T[] =>
  groupByKey(items, toGroupKey).flatMap((group) =>
    [...group].sort((a, b) =>
      compareLowestPack(toLowestPackKey(a), toLowestPackKey(b)),
    ),
  );

// Groups by product, then sorts each group using the same canonical /
// supplier-configured pack order as the rest of the app (comparePacksByUnit).
// Use this when auto_select_lowest_uom is off, so a scan multi-match chooser
// shows the same pack order as the catalog and order guide.
export const groupAndSortByPackOrder = <T>(
  items: T[],
  toGroupKey: (item: T) => string,
  toPackSortKey: (item: T) => PackSortKey,
  config: ResolvedPackSort,
): T[] =>
  groupByKey(items, toGroupKey).flatMap((group) =>
    [...group].sort((a, b) =>
      comparePacksByUnit(toPackSortKey(a), toPackSortKey(b), config),
    ),
  );

// Shared by the on-device (scan-results) and local-scan (local-scanner-context)
// multi-match choosers, which both need to group+sort the same way: lowest
// UOM first when auto_select_lowest_uom is on (matching what auto-select
// would have picked), otherwise the canonical / supplier-configured pack
// order so the chooser matches the catalog and order guide.
export const groupSortMultiMatchResults = <T>(
  items: T[],
  toGroupKey: (item: T) => string,
  toPackKey: (item: T) => LowestPackKey,
  autoSelectLowestUom: boolean,
  packSortConfig: ResolvedPackSort,
): T[] =>
  autoSelectLowestUom
    ? groupAndSortByLowestPack(items, toGroupKey, toPackKey)
    : groupAndSortByPackOrder(items, toGroupKey, toPackKey, packSortConfig);
